import { defineStore } from 'pinia'
import { ref } from 'vue'

import api from '@/client/core/api'
import {
  InstagramInstantwinEnqueteAnswer,
  InstagramInstantwinEnqueteQuestion,
  InstagramInstantwinMessageSendCountRow,
  MessageSendCountSortBy
} from '@/client/features/instagram_instantwin_campaign_analytics/types'
import { useSystemStore } from '@/client/features/system/hooks/systemStore'
import { TCampaign, TCampaignPrize } from '@/typings/api/publics/campaigns.type'
import {
  TGetInstagramInstantwinCampaignAnalyticsParams as TParams,
  TInstagramInstantwinCampaignAnalytic
} from '@/typings/api/publics/instagram_instantwin_campaign_analytics.type'

export const useInstagramInstantwinAnalyticsPublicStore = defineStore('InstagramInstantwinAnalyticsPublicStore', () => {
  const systemStore = useSystemStore()

  const initialParams: TParams = {
    hash: '',
    prizeId: '',
    startDatetime: '',
    endDatetime: ''
  }

  const campaignAnalytics = ref<TInstagramInstantwinCampaignAnalytic[]>([])
  const campaign = ref<TCampaign | null>(null)
  const prizes = ref<TCampaignPrize[]>([])
  const params = ref<TParams>({ ...initialParams })
  const enqueteQuestions = ref<InstagramInstantwinEnqueteQuestion[]>([])
  const enqueteSelectedId = ref<number | null>(null)
  const enqueteAnswers = ref<InstagramInstantwinEnqueteAnswer[]>([])
  const enqueteTotalCount = ref(0)
  const messageSendCountRows = ref<InstagramInstantwinMessageSendCountRow[]>([])
  const messageSendCountTotal = ref(0)
  const messageSendCountPagingNo = ref(1)
  const messageSendCountLimitCount = ref(25)
  const messageSendCountSortBy = ref<MessageSendCountSortBy | ''>('')
  const enqueteLoading = ref(false)
  const messageSendCountLoading = ref(false)
  const error = ref(false)
  const loading = ref(false)

  const setLoading = (value: boolean) => {
    loading.value = value
  }

  const setError = (value: boolean) => {
    error.value = value
  }

  const fetchCampaignAnalytics = async () => {
    setError(false)
    setLoading(true)
    systemStore.setLoading(true)

    try {
      const result = await api.get('/publics/instagram_instantwin_campaign_analytics', { params: params.value })

      if (!result.data) {
        setError(true)
        return
      }

      campaignAnalytics.value = result.data
    } catch {
      setError(true)
    } finally {
      setLoading(false)
      systemStore.setLoading(false)
    }
  }

  const resetEnqueteState = () => {
    enqueteQuestions.value = []
    enqueteSelectedId.value = null
    enqueteAnswers.value = []
    enqueteTotalCount.value = 0
  }

  const resetMessageSendCountState = () => {
    messageSendCountRows.value = []
    messageSendCountTotal.value = 0
    messageSendCountPagingNo.value = 1
    messageSendCountSortBy.value = ''
  }

  const resetAdditionalAnalytics = () => {
    resetEnqueteState()
    resetMessageSendCountState()
  }

  const normalizePrizeId = (value: number | '') => {
    if (!value) {
      return null
    }
    const prizeId = Number(value)
    return Number.isFinite(prizeId) && prizeId > 0 ? prizeId : null
  }

  const fetchEnqueteAnalytics = async () => {
    setError(false)
    const prizeId = normalizePrizeId(params.value.prizeId)
    if (!params.value.hash || !params.value.startDatetime || !params.value.endDatetime || !prizeId) {
      resetEnqueteState()
      return null
    }

    enqueteLoading.value = true
    try {
      const result = await api.get('/publics/instagram_instantwin_campaign_analytics/questionnaire', {
        params: {
          hash: params.value.hash,
          prizeId,
          startDatetime: params.value.startDatetime,
          endDatetime: params.value.endDatetime,
          selectNodeId: enqueteSelectedId.value ?? undefined
        }
      })

      if (!result.data) {
        setError(true)
        resetEnqueteState()
        return null
      }

      enqueteQuestions.value = result.data.questions
      enqueteSelectedId.value = result.data.selectedQuestion?.id ?? null
      enqueteAnswers.value = result.data.answers
      enqueteTotalCount.value = result.data.totalCount
      return result.data
    } catch {
      setError(true)
      resetEnqueteState()
      return null
    } finally {
      enqueteLoading.value = false
    }
  }

  const fetchMessageSendCounts = async (forCsv = false) => {
    setError(false)
    const prizeId = normalizePrizeId(params.value.prizeId)
    if (!params.value.hash || !params.value.startDatetime || !params.value.endDatetime || !prizeId) {
      resetMessageSendCountState()
      return null
    }

    if (!forCsv) {
      messageSendCountLoading.value = true
    }

    try {
      const requestParams: Record<string, unknown> = {
        hash: params.value.hash,
        prizeId,
        startDatetime: params.value.startDatetime,
        endDatetime: params.value.endDatetime,
        forCsv
      }

      if (messageSendCountSortBy.value) {
        requestParams.sortBy = messageSendCountSortBy.value
      }

      if (!forCsv) {
        requestParams.pagingNo = messageSendCountPagingNo.value
        requestParams.limitCount = messageSendCountLimitCount.value
      }

      const result = await api.get('/publics/instagram_instantwin_campaign_analytics/message_send_counts', {
        params: requestParams
      })

      if (!result.data) {
        setError(true)
        resetMessageSendCountState()
        return null
      }

      if (!forCsv) {
        messageSendCountRows.value = result.data.rows
        messageSendCountTotal.value = result.data.dataCount
      }

      return result.data
    } catch {
      setError(true)
      resetMessageSendCountState()
      return null
    } finally {
      if (!forCsv) {
        messageSendCountLoading.value = false
      }
    }
  }

  const exportMessageSendCountsCsv = async () => {
    return fetchMessageSendCounts(true)
  }

  const fetchCampaign = async () => {
    setError(false)

    try {
      const result = await api.get(`/publics/campaigns/${params.value.hash}`)

      if (!result.data) {
        setError(true)
        return
      }

      campaign.value = result.data
    } catch {
      setError(true)
    }
  }

  const fetchPrizes = async () => {
    setError(false)

    try {
      const result = await api.get(`/publics/campaigns/${params.value.hash}/prizes`)

      if (!result.data) {
        setError(true)
        return
      }

      prizes.value = result.data
    } catch {
      setError(true)
    }
  }

  const setParams = (payload: Partial<TParams>) => {
    const prevPrizeId = params.value.prizeId
    params.value = { ...params.value, ...payload }

    if (payload.prizeId !== undefined && payload.prizeId !== prevPrizeId) {
      resetAdditionalAnalytics()
    }
  }

  const setEnqueteSelectedId = (value: number | null) => {
    enqueteSelectedId.value = value
  }

  const setMessageSendCountSortBy = (value: MessageSendCountSortBy | '') => {
    messageSendCountSortBy.value = value
    messageSendCountPagingNo.value = 1
  }

  const setMessageSendCountPaging = (pagingNo: number, limitCount?: number) => {
    messageSendCountPagingNo.value = pagingNo
    if (typeof limitCount === 'number') {
      messageSendCountLimitCount.value = limitCount
    }
  }

  const reset = () => {
    campaignAnalytics.value = []
    campaign.value = null
    prizes.value = []
    params.value = { ...initialParams }
    resetAdditionalAnalytics()
    messageSendCountLimitCount.value = 25
    error.value = false
    loading.value = false
  }

  return {
    campaignAnalytics,
    campaign,
    prizes,
    params,
    enqueteQuestions,
    enqueteSelectedId,
    enqueteAnswers,
    enqueteTotalCount,
    messageSendCountRows,
    messageSendCountTotal,
    messageSendCountPagingNo,
    messageSendCountLimitCount,
    messageSendCountSortBy,
    enqueteLoading,
    messageSendCountLoading,
    error,
    loading,
    fetchCampaignAnalytics,
    fetchEnqueteAnalytics,
    fetchMessageSendCounts,
    exportMessageSendCountsCsv,
    fetchCampaign,
    fetchPrizes,
    setParams,
    setEnqueteSelectedId,
    setMessageSendCountSortBy,
    setMessageSendCountPaging,
    reset
  }
})
